import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Stack,
  Text,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import KeepChattingWarningDialog from "./KeepChattingWarningDialog/KeepChattingWarningDialog";
import FileProgressBar from "./FileProgressBar";
import style from "./DocumentUploadDialog.module.css";
import { UserUploadConfig } from "../../pages/chat/types";
import { useUseCaseContext } from "../../hooks/UseCaseContext";
import { ChatMessage } from "../../api";

export interface FileStatus {
  file: File | { name: string };
  status: "pending" | "uploading" | "completed" | "error" | "processing";
  progress: number;
  error?: string;
  uploadedFileName?: string;
  processingStatus?: "processing" | "completed" | "error";
  processingMessage?: string;
  processingStartTime?: number;
  timeRemaining?: number;
  elapsedTime?: number;
  estimatedTotalTime?: number;
  creationTime?: Date;
}

interface FileValidationError {
  fileName: string;
  type: "size" | "extension" | "limit" | "special_chars";
  extension?: string;
}

interface DocumentUploadDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  conversationId: string;
  onUploadProgress: (fileStatuses: FileStatus[], isUploading: boolean) => void;
  onUploadComplete: () => void;
  onFileDeleted?: (uploadedFileName: string) => void;
  uploadedFiles: FileStatus[];
  userUploadConfig?: UserUploadConfig;
  answers: ChatMessage[];
}

const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  isOpen,
  onDismiss,
  conversationId,
  onUploadProgress,
  onUploadComplete,
  onFileDeleted,
  uploadedFiles,
  userUploadConfig,
  answers,
}) => {
  const { useCase } = useUseCaseContext();
  const [fileStatuses, setFileStatuses] = useState<FileStatus[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    FileValidationError[]
  >([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Use config values with fallback to defaults
  const MAX_FILES = userUploadConfig ? userUploadConfig.maxFiles : 5;
  const MAX_FILE_SIZE_MB = userUploadConfig ? userUploadConfig.maxFileSize : 20;
  const ALLOWED_FILE_TYPES = [
    {
      file_type: "application/pdf",
      display_names: "PDF",
    },
    {
      file_type:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      display_names: "DOCX",
    },
    {
      file_type: "image/*",
      display_names: "JPG, JPEG, PNG",
    },
    {
      file_type:
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      display_names: "PPTX",
    },
    {
      file_type: "application/vnd.ms-powerpoint",
      display_names: "PPT",
    },
    {
      file_type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      display_names: "XLSX",
    },
    {
      file_type: "application/vnd.ms-excel",
      display_names: "XLS",
    },
    {
      file_type: "application/vnd.ms-outlook",
      display_names: "MSG",
    },
  ];

  useEffect(() => {
    setFileStatuses([]);
    setValidationErrors([]);
    setIsUploading(false);
    onUploadProgress([], false);
  }, [conversationId]);

  useEffect(() => {
    setFileStatuses(uploadedFiles);
  }, [uploadedFiles]);

  const uploadState = useMemo(() => {
    if (fileStatuses) {
      if (!fileStatuses.length) return "initial";
      if (
        fileStatuses.some(
          (f) => f.status === "uploading" || f.status === "processing",
        )
      )
        return "uploading";
      if (fileStatuses.every((f) => f.status === "completed"))
        return "completed";
      return "pending";
    }
  }, [fileStatuses]);

  const dialogTitle = useMemo(() => {
    switch (uploadState) {
      case "uploading":
        return "Uploading file(s)...";
      case "completed":
        return "Manage file(s)";
      case "pending":
        return "Selected file(s)";
      default:
        return "Select file(s)";
    }
  }, [uploadState]);

  const validateFile = (file: File): FileValidationError | null => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return {
        fileName: file.name,
        type: "size",
      };
    }

    if (
      file.type &&
      !ALLOWED_FILE_TYPES.some(
        (type) =>
          file.type === type.file_type ||
          (type.file_type === "image/*" && file.type.startsWith("image/")),
      )
    ) {
      const extension = file.name.split(".").pop() || "unknown";
      return {
        fileName: file.name,
        type: "extension",
        extension,
      };
    }

    return null;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const newErrors: FileValidationError[] = [];
    const validFiles: FileStatus[] = [];
    const currentFileCount = fileStatuses.length;

    files.forEach((file, index) => {
      // Check if adding this file would exceed MAX_FILES limit
      if (currentFileCount + validFiles.length >= MAX_FILES) {
        newErrors.push({
          fileName: file.name,
          type: "limit",
        });
        return;
      }

      const error = validateFile(file);
      if (error) {
        newErrors.push(error);
      } else {
        validFiles.push({
          file,
          status: "pending" as const,
          progress: 0,
        });
      }
    });

    // Add general error message if files were rejected due to limit
    const filesRejectedForLimit = newErrors.filter(
      (e) => e.type === "limit",
    ).length;
    if (filesRejectedForLimit > 0) {
      const remainingSlots = Math.max(0, MAX_FILES - currentFileCount);
      if (remainingSlots === 0) {
        newErrors.push({
          fileName: `Maximum ${MAX_FILES} files allowed. Remove some files before adding more.`,
          type: "limit",
        });
      } else {
        newErrors.push({
          fileName: `Only ${remainingSlots} more file(s) can be added. Maximum ${MAX_FILES} files allowed.`,
          type: "limit",
        });
      }
    }

    setValidationErrors(newErrors);
    setFileStatuses([...fileStatuses, ...validFiles]);

    // Clear the input so the same files can be selected again if needed
    event.target.value = "";
  };

  const uploadSingleFile = async (
    fileStatus: FileStatus,
    onProgress: (progress: number) => void,
    onUploadComplete?: () => void,
  ) => {
    const formData = new FormData();
    formData.append("files", fileStatus.file as File);
    formData.append("conversation_id", conversationId);
    formData.append("use_case", useCase);

    if (answers && answers.length == 2) {
      formData.append("confirmation_message", JSON.stringify(answers));
    }
    try {
      const xhr = new XMLHttpRequest();

      return new Promise<{ success: boolean; uploadedFileName?: string }>(
        (resolve) => {
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded * 100) / event.total);
              onProgress(progress);

              // When upload is complete, notify that processing is starting
              if (progress === 100 && onUploadComplete) {
                setTimeout(() => {
                  onUploadComplete();
                }, 100);
              }
            }
          });

          xhr.addEventListener("load", async () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const response = JSON.parse(xhr.responseText);
                // Extract the uploaded filename from the response
                const uploadedFileName = response.files?.[0]?.[1]; // [original, uploaded] format
                resolve({ success: true, uploadedFileName });
              } catch (e) {
                resolve({ success: false });
              }
            } else {
              resolve({ success: false });
            }
          });

          xhr.addEventListener("error", () => {
            resolve({ success: false });
          });

          xhr.open("POST", "/api/upload");
          xhr.send(formData);
        },
      );
    } catch (error) {
      return { success: false };
    }
  };

  const calculateEstimatedProcessingTime = (fileSizeBytes: number): number => {
    // Average bytes per page
    const bytesPerPage = 35000;

    // Get processing rate for the model
    const pagesPerMinute = 50; // ada-002 rate

    // Calculate estimated pages and time
    const estimatedPages = Math.max(1, fileSizeBytes / bytesPerPage);
    const estimatedMinutes = estimatedPages / pagesPerMinute;

    // Add overhead for chunking, vectorisation, and indexing
    const overheadFactor = 1.3;
    const totalEstimatedSeconds = estimatedMinutes * 60 * overheadFactor;

    // Minimum time of 60 seconds, maximum of 8 minutes
    return Math.max(60, Math.min(480, Math.floor(totalEstimatedSeconds)));
  };

  const startTimeEstimation = (fileIndex: number, fileSize: number) => {
    const estimatedTime = calculateEstimatedProcessingTime(fileSize);
    const startTime = Date.now();

    // Update initial processing state with estimated time
    setFileStatuses((prev) =>
      prev.map((status, idx) =>
        idx === fileIndex
          ? {
              ...status,
              processingStartTime: startTime / 1000,
              estimatedTotalTime: estimatedTime,
              timeRemaining: estimatedTime,
              elapsedTime: 0,
            }
          : status,
      ),
    );

    // Update time remaining and progress every second
    const updateTimer = setInterval(() => {
      setFileStatuses((prev) => {
        const updatedStatuses = prev.map((status, idx) => {
          if (idx === fileIndex && status.status === "processing") {
            const now = Date.now();
            const elapsedSeconds = Math.floor((now - startTime) / 1000);
            const remainingSeconds = Math.max(
              0,
              estimatedTime - elapsedSeconds,
            );

            return {
              ...status,
              elapsedTime: elapsedSeconds,
              timeRemaining: remainingSeconds,
              processingMessage: "Processing embeddings",
            };
          }
          return status;
        });

        // Clear timer if file is no longer processing
        const stillProcessing =
          updatedStatuses[fileIndex]?.status === "processing";
        if (!stillProcessing) {
          clearInterval(updateTimer);
        }

        return updatedStatuses;
      });
    }, 1000);

    // Auto-clear timer after estimated time + buffer
    setTimeout(
      () => {
        clearInterval(updateTimer);
      },
      (estimatedTime + 60) * 1000,
    ); // Extra minute buffer
  };

  const handleUpload = async () => {
    setValidationErrors([]);
    if (fileStatuses.length === 0) return;
    setIsUploading(true);

    let currentFileStatuses = [...fileStatuses];

    onUploadProgress(currentFileStatuses, true);

    for (let i = 0; i < currentFileStatuses.length; i++) {
      if (currentFileStatuses[i].status !== "pending") continue;

      currentFileStatuses = currentFileStatuses.map((status, idx) =>
        idx === i ? { ...status, status: "uploading", progress: 0 } : status,
      );
      setFileStatuses(currentFileStatuses);
      onUploadProgress(currentFileStatuses, true);

      const result = await uploadSingleFile(
        currentFileStatuses[i],
        (progress) => {
          currentFileStatuses = currentFileStatuses.map((status, idx) =>
            idx === i ? { ...status, progress } : status,
          );
          setFileStatuses(currentFileStatuses);
          onUploadProgress(currentFileStatuses, true);
        },
        () => {
          // When upload completes, transition to processing
          currentFileStatuses = currentFileStatuses.map((status, idx) =>
            idx === i
              ? {
                  ...status,
                  status: "processing" as const,
                  processingStartTime: Date.now() / 1000,
                  processingMessage: "Processing embeddings",
                }
              : status,
          );
          setFileStatuses(currentFileStatuses);
          onUploadProgress(currentFileStatuses, true);

          // Start time estimation with progress updates
          const fileSize = (currentFileStatuses[i].file as File).size || 0;
          startTimeEstimation(i, fileSize);
        },
      );

      // When server responds and processing is complete, update to completed status
      currentFileStatuses = currentFileStatuses.map((status, idx) =>
        idx === i
          ? {
              ...status,
              status: result.success ? "completed" : "error",
              progress: result.success ? 100 : status.progress,
              uploadedFileName: result.uploadedFileName,
              processingMessage: result.success
                ? "Processing complete"
                : "Processing failed",
              timeRemaining: 0,
            }
          : status,
      );
      setFileStatuses(currentFileStatuses);
      onUploadProgress(currentFileStatuses, true);
    }

    setIsUploading(false);
    onUploadProgress(currentFileStatuses, false);

    // Check if all files were uploaded successfully
    const allCompleted = currentFileStatuses.every(
      (status) => status.status === "completed",
    );

    if (allCompleted) {
      onUploadComplete();
    }
  };

  const handleDeleteFile = async (fileIndex: number) => {
    const fileToDelete = fileStatuses[fileIndex];
    if (!fileToDelete.uploadedFileName) return;

    setDeleteError(null);

    try {
      const response = await fetch("/api/delete_file", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_name: fileToDelete.uploadedFileName,
          conversation_id: conversationId,
        }),
      });

      if (response.ok) {
        const uploadedFileName = fileToDelete.uploadedFileName;
        const updatedFileStatuses = fileStatuses.filter(
          (_, index) => index !== fileIndex,
        );
        setFileStatuses(updatedFileStatuses);
        onUploadProgress(updatedFileStatuses, isUploading);
        onFileDeleted?.(uploadedFileName);
        onUploadComplete();
      } else {
        const errorData = await response.json();
        setDeleteError(errorData.error || "Failed to delete file.");
      }
    } catch (error) {
      setDeleteError("An unexpected error occurred while deleting the file.");
    }
  };

  const handleViewFile = async (fileIndex: number) => {
    const fileToOpen = fileStatuses[fileIndex];
    if (!fileToOpen.uploadedFileName) return;
    const response = await fetch(
      `/api/get_file?file_name=${encodeURIComponent(
        fileToOpen.uploadedFileName,
      )}`,
    );
    if (response.ok) {
      const blobURL = await response.json().then((data) => data.file_url);
      // Open the file URL in a new tab
      window.open(blobURL, "_blank");
    } else {
      console.error("Failed to view file");
    }
  };

  const handleCancel = () => {
    if (uploadState === "uploading") {
      handleKeepChatting();
    } else if (uploadState !== "completed") {
      setFileStatuses(
        fileStatuses.filter((status) => status.status === "completed"),
      );
    }
    setValidationErrors([]);
    onDismiss();
  };

  const handleKeepChatting = () => {
    setShowWarningDialog(true);
  };

  const handleWarningConfirm = () => {
    setShowWarningDialog(false);
    onDismiss();
  };

  const handleWarningCancel = () => {
    setShowWarningDialog(false);
  };

  const renderFooterButtons = () => {
    switch (uploadState) {
      case "uploading":
        return (
          <DefaultButton
            onClick={handleKeepChatting}
            text="Keep chatting"
            className={style.btn}
          />
        );
      case "completed":
        const remainingSlots = MAX_FILES - fileStatuses.length;
        const canUploadMore = remainingSlots > 0;

        return (
          <>
            <PrimaryButton
              onClick={() => document.getElementById("file-upload")?.click()}
              text={"Upload more files"}
              className={style.btn}
              disabled={!canUploadMore}
            />
            <DefaultButton
              onClick={handleCancel}
              text="Close"
              className={style.btn}
            />
          </>
        );
      case "pending":
        return (
          <>
            <PrimaryButton
              text="Upload"
              onClick={handleUpload}
              className={style.btn}
            />
            <DefaultButton
              onClick={handleCancel}
              text="Cancel"
              className={style.btn}
            />
          </>
        );
      default:
        return (
          <>
            <PrimaryButton
              text="Select"
              onClick={() => document.getElementById("file-upload")?.click()}
              className={style.btn}
            />
            <DefaultButton
              onClick={handleCancel}
              text="Cancel"
              className={style.btn}
            />
          </>
        );
    }
  };

  const renderValidationErrors = () => {
    return validationErrors.map((error, index) => {
      let message = "";
      if (error.type === "size") {
        message = `"${error.fileName}" is not selected. File size too large.`;
      } else if (error.type === "special_chars") {
        message = `"${error.fileName}" is not selected. File name contains special characters. Please rename the file using only letters, numbers, spaces, hyphens, underscores, or dots.`;
      } else if (error.type === "extension") {
        message = `"${error.fileName}" is not selected. Unsupported File extension.`;
      } else if (error.type === "limit") {
        // Check if this is a general limit message or individual file limit
        if (
          error.fileName.includes("Maximum") ||
          error.fileName.includes("Only")
        ) {
          message = error.fileName;
        } else {
          message = `"${error.fileName}" is not selected. File upload limit reached.`;
        }
      }

      return (
        <MessageBar
          key={index}
          messageBarType={MessageBarType.warning}
          isMultiline={true}
          dismissButtonAriaLabel="Close"
          onDismiss={() => {
            setValidationErrors((prev) => prev.filter((_, i) => i !== index));
          }}
        >
          {message}
        </MessageBar>
      );
    });
  };

  return (
    <>
      <Dialog
        isBlocking
        hidden={!isOpen}
        onDismiss={handleCancel}
        dialogContentProps={{
          type: DialogType.normal,
          title: dialogTitle,
        }}
      >
        {uploadState === "uploading" &&
          "Large files might take a few minutes to process."}
        <Stack tokens={{ childrenGap: 10 }}>
          <input
            type="file"
            id="file-upload"
            style={{ display: "none" }}
            onChange={handleFileChange}
            multiple
            accept={ALLOWED_FILE_TYPES.map((type) => type.file_type).join(",")}
          />
          {fileStatuses && fileStatuses.length > 0 ? (
            <Stack tokens={{ childrenGap: 8 }}>
              {fileStatuses.map((fileStatus, index) => (
                <FileProgressBar
                  key={index}
                  fileName={fileStatus.file.name}
                  progress={fileStatus.progress}
                  status={fileStatus.status}
                  error={fileStatus.error}
                  processingMessage={fileStatus.processingMessage}
                  timeRemaining={fileStatus.timeRemaining}
                  elapsedTime={fileStatus.elapsedTime}
                  estimatedTotalTime={fileStatus.estimatedTotalTime}
                  creationTime={fileStatus.creationTime}
                  onViewFile={
                    fileStatus.status === "completed"
                      ? () => handleViewFile(index)
                      : undefined
                  }
                  onDelete={
                    fileStatus.status === "completed"
                      ? () => handleDeleteFile(index)
                      : undefined
                  }
                />
              ))}
            </Stack>
          ) : (
            <>
              <Text>
                You can select up to {MAX_FILES} files, each not exceeding{" "}
                {MAX_FILE_SIZE_MB}MB.
              </Text>
              <Text variant="small">
                Supported file types:{" "}
                {ALLOWED_FILE_TYPES.map((type) => type.display_names).join(
                  ", ",
                )}
              </Text>
              <Text variant="small">
                Please note: <br />
                - Images within .docx files are not supported. However, the
                chatbot can still process the content of the file, excluding the
                images. <br />- Large files may take a few minutes to process.
              </Text>
            </>
          )}
          {validationErrors.length > 0 && (
            <Stack tokens={{ childrenGap: 8 }}>
              {renderValidationErrors()}
            </Stack>
          )}
          {deleteError && (
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={true}
              dismissButtonAriaLabel="Close"
              onDismiss={() => setDeleteError(null)}
            >
              {deleteError}
            </MessageBar>
          )}
        </Stack>
        <DialogFooter>{renderFooterButtons()}</DialogFooter>
      </Dialog>
      <KeepChattingWarningDialog
        isOpen={showWarningDialog}
        onConfirm={handleWarningConfirm}
        onCancel={handleWarningCancel}
      />
    </>
  );
};

export default DocumentUploadDialog;
